import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Trash2, Check, RotateCcw } from 'lucide-react';
import CameraModal from './CameraModal';
import '../styles/AvatarModal.css';

// ─── Компонент обрезки ────────────────────────────────────────────────────────
function CropEditor({ imageUrl, onConfirm, onCancel }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 }); // размер отображаемого img
  const [circle, setCircle] = useState(null); // { x, y, r } в px контейнера
  const dragRef = useRef(null); // { mode: 'move'|'resize', startX, startY, startCx, startCy, startR }

  // Инициализируем круг по центру изображения когда оно загрузилось
  useEffect(() => {
    if (!imgRef.current) return;
    const measure = () => {
      const rect = imgRef.current.getBoundingClientRect();
      const contRect = containerRef.current.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      const offX = rect.left - contRect.left;
      const offY = rect.top - contRect.top;
      setImgSize({ w, h, offX, offY });
      const r = Math.min(w, h) * 0.42;
      setCircle({ x: offX + w / 2, y: offY + h / 2, r });
    };
    if (imgRef.current.complete) {
      measure();
    } else {
      imgRef.current.addEventListener('load', measure);
      return () => imgRef.current?.removeEventListener('load', measure);
    }
  }, [imageUrl]);

  const getPointerPos = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const onPointerDown = useCallback((e) => {
    if (!circle) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    const dx = pos.x - circle.x;
    const dy = pos.y - circle.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const mode = dist < circle.r - 14 ? 'move' : dist < circle.r + 18 ? 'resize' : null;
    if (!mode) return;
    dragRef.current = { mode, startX: pos.x, startY: pos.y, startCx: circle.x, startCy: circle.y, startR: circle.r };
  }, [circle]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current || !circle) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    const { mode, startX, startY, startCx, startCy, startR } = dragRef.current;
    const { w, h, offX = 0, offY = 0 } = imgSize;
    const minR = 30;
    const maxR = Math.min(w, h) / 2;

    if (mode === 'move') {
      let nx = startCx + (pos.x - startX);
      let ny = startCy + (pos.y - startY);
      // Ограничиваем по границам изображения
      nx = Math.max(offX + circle.r, Math.min(offX + w - circle.r, nx));
      ny = Math.max(offY + circle.r, Math.min(offY + h - circle.r, ny));
      setCircle(c => ({ ...c, x: nx, y: ny }));
    } else {
      const dx = pos.x - circle.x;
      const dy = pos.y - circle.y;
      let nr = Math.sqrt(dx * dx + dy * dy);
      nr = Math.max(minR, Math.min(maxR, nr));
      // Ограничиваем чтобы круг не выходил за изображение
      const maxByPos = Math.min(
        circle.x - offX,
        offX + w - circle.x,
        circle.y - offY,
        offY + h - circle.y
      );
      nr = Math.min(nr, maxByPos);
      setCircle(c => ({ ...c, r: Math.max(minR, nr) }));
    }
  }, [circle, imgSize]);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // Применяем кроп и отдаём наверх как File
  const handleConfirm = () => {
    if (!circle || !imgRef.current) return;
    const img = imgRef.current;
    const contRect = containerRef.current.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Масштаб: реальный пиксель / отображаемый пиксель
    const scaleX = img.naturalWidth / imgRect.width;
    const scaleY = img.naturalHeight / imgRect.height;

    // Координаты круга в пикселях реального изображения
    const realCx = (circle.x - (imgRect.left - contRect.left)) * scaleX;
    const realCy = (circle.y - (imgRect.top - contRect.top)) * scaleY;
    const realR = circle.r * ((scaleX + scaleY) / 2);

    const outputSize = 400;
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');

    // Круглая маска
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();

    // Рисуем обрезанный регион
    ctx.drawImage(
      img,
      realCx - realR, realCy - realR, realR * 2, realR * 2,
      0, 0, outputSize, outputSize
    );

    canvas.toBlob((blob) => {
      const file = new File([blob], `avatar_crop_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const preview = URL.createObjectURL(blob);
      onConfirm(file, preview);
    }, 'image/jpeg', 0.9);
  };

  if (!imageUrl) return null;

  return (
    <div className="crop-editor">
      <div className="crop-hint">Перетащите круг или измените его размер за край</div>
      <div
        className="crop-container"
        ref={containerRef}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt="crop"
          className="crop-source-img"
          draggable={false}
        />
        {circle && (
          <svg className="crop-svg">
            <defs>
              <mask id="cropMask">
                <rect width="100%" height="100%" fill="white" />
                <circle cx={circle.x} cy={circle.y} r={circle.r} fill="black" />
              </mask>
            </defs>
            {/* Затемнение снаружи круга */}
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.52)" mask="url(#cropMask)" />
            {/* Пунктирный белый круг */}
            <circle
              cx={circle.x} cy={circle.y} r={circle.r}
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeDasharray="10 5"
            />
            {/* Ручка ресайза (снизу) */}
            <circle
              cx={circle.x}
              cy={circle.y + circle.r}
              r={9}
              fill="white"
              opacity="0.9"
            />
            <line
              x1={circle.x - 4} y1={circle.y + circle.r}
              x2={circle.x + 4} y2={circle.y + circle.r}
              stroke="#555" strokeWidth="1.5"
            />
          </svg>
        )}
      </div>
      <div className="crop-actions">
        <button className="btn-crop-cancel" onClick={onCancel}>
          <RotateCcw size={16} strokeWidth={2} />
          Переснять
        </button>
        <button className="btn-crop-confirm" onClick={handleConfirm}>
          <Check size={16} strokeWidth={2.5} />
          Применить
        </button>
      </div>
    </div>
  );
}


// ─── Главный компонент AvatarModal ────────────────────────────────────────────
function AvatarModal({ currentAvatar, displayName, onClose, onUpload, onDelete }) {
  const [rawFile, setRawFile] = useState(null);
  const [rawPreview, setRawPreview] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const capturedRef = useRef(false);

  const showCrop = rawPreview && !croppedFile;

  const handleCameraCapture = (photos) => {
    if (photos.length > 0) {
      capturedRef.current = true;
      const { file, preview } = photos[0];
      setRawFile(file);
      setRawPreview(preview);
      setCroppedFile(null);
      setCroppedPreview(null);
    }
    setIsCameraOpen(false);
  };

  const handleCameraClose = () => {
    setIsCameraOpen(false);
    if (!capturedRef.current) onClose();
  };

  const handleCropConfirm = (file, preview) => {
    setCroppedFile(file);
    setCroppedPreview(preview);
  };

  const handleCropCancel = () => {
    capturedRef.current = false;
    setRawFile(null);
    setRawPreview(null);
    setCroppedFile(null);
    setCroppedPreview(null);
    setIsCameraOpen(true);
  };

  const handleSave = async () => {
    const fileToUpload = croppedFile;
    if (!fileToUpload) return;
    setIsUploading(true);
    try {
      await onUpload(fileToUpload);
      onClose();
    } catch (err) {
      alert('Ошибка загрузки фото');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить фото профиля?')) return;
    try {
      await onDelete();
      onClose();
    } catch (err) {
      alert('Ошибка удаления фото');
    }
  };

  if (isCameraOpen) {
    return (
      <CameraModal
        isOpen={true}
        onClose={handleCameraClose}
        onCapture={handleCameraCapture}
        photoOnly={true}
        frontOnly={true}
      />
    );
  }

  // Режим кропа
  if (showCrop) {
    return (
      <div className="avatar-modal-overlay">
        <div className="avatar-modal avatar-modal-crop">
          <div className="avatar-modal-header">
            <h3>Выберите область</h3>
            <button className="btn-avatar-close" onClick={handleCropCancel}>
              <X size={18} strokeWidth={2} />
            </button>
          </div>
          <CropEditor
            imageUrl={rawPreview}
            onConfirm={handleCropConfirm}
            onCancel={handleCropCancel}
          />
        </div>
      </div>
    );
  }

  // Режим подтверждения сохранения
  return (
    <div className="avatar-modal-overlay" onClick={onClose}>
      <div className="avatar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="avatar-modal-header">
          <h3>Фото профиля</h3>
          <button className="btn-avatar-close" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="avatar-modal-body">
          <div className="avatar-preview-wrap">
            {croppedPreview ? (
              <img src={croppedPreview} alt="avatar" className="avatar-preview-img" />
            ) : currentAvatar ? (
              <img src={currentAvatar} alt="avatar" className="avatar-preview-img" />
            ) : null}
          </div>
        </div>

        <div className="avatar-modal-footer">
          {currentAvatar && !croppedFile && (
            <button className="btn-avatar-delete" onClick={handleDelete}>
              <Trash2 size={16} strokeWidth={1.8} />
              Удалить фото
            </button>
          )}
          {croppedFile && (
            <button
              className="btn-avatar-save"
              onClick={handleSave}
              disabled={isUploading}
            >
              {isUploading ? 'Сохранение...' : 'Сохранить'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AvatarModal;
